import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Award, Plus, ShieldCheck, Calendar, IndianRupee, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const VENDORS = ["VMware", "AWS", "Cisco", "Microsoft", "Google Cloud", "Docker", "CNCF", "AXELOS", "Oracle", "Red Hat", "Other"];

function daysUntil(iso) {
  const diff = Math.floor((new Date(iso) - new Date()) / 86400000);
  return diff;
}

function fmt(d) { try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } }

const STATUS_STYLE = {
  "Reimbursed": "bg-emerald-50 text-emerald-700",
  "Approved": "bg-red-50 text-[#DC2626]",
  "Pending": "bg-orange-50 text-orange-700",
  "Rejected": "bg-red-50 text-red-700",
  "Not Requested": "bg-slate-100 text-slate-600",
};

export default function Certifications() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", vendor: "VMware", issued_date: "", expires_date: "", credential_id: "", exam_fee: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (user?.employee_id) setMine((await api.get("/certifications/mine")).data);
    if (isReviewer) setAll((await api.get("/certifications/all")).data);
  };
  useEffect(() => { load(); }, [user?.employee_id, isReviewer]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.issued_date || !form.expires_date) { toast.error("Fill required fields"); return; }
    setBusy(true);
    try {
      await api.post("/certifications", { ...form, exam_fee: Number(form.exam_fee || 0) });
      toast.success("Certification added");
      setShowForm(false);
      setForm({ name: "", vendor: "VMware", issued_date: "", expires_date: "", credential_id: "", exam_fee: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const decide = async (id, status) => {
    try {
      await api.put(`/certifications/${id}/reimburse`, { status });
      toast.success(`Reimbursement ${status.toLowerCase()}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const source = isReviewer ? all : mine;
  const totalReimbursed = source.filter(c => c.reimbursement_status === "Reimbursed").reduce((s, c) => s + c.exam_fee, 0);
  const expiringSoon = source.filter(c => { const d = daysUntil(c.expires_date); return d >= 0 && d <= 90; });

  return (
    <div className="space-y-6" data-testid="certs-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Certifications</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Grow the badge wall.</h1>
          <p className="mt-1 text-sm text-slate-500">Track every VMware, AWS, Cisco and Microsoft cert with renewal dates & reimbursements.</p>
        </div>
        {user?.employee_id && (
          <Button onClick={() => setShowForm(v => !v)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="add-cert-btn">
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> {showForm ? "Close" : "Add certification"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={ShieldCheck} label="Total certifications" value={source.length} tone="blue" />
        <MetricCard icon={AlertTriangle} label="Expiring in 90d" value={expiringSoon.length} tone="orange" />
        <MetricCard icon={IndianRupee} label="Reimbursed (total)" value={"₹ " + totalReimbursed.toLocaleString("en-IN")} tone="green" />
        <MetricCard icon={Award} label="Top vendor" value={topVendor(source)} tone="slate" />
      </div>

      {showForm && (
        <Card className="p-6 rounded-xl border-slate-200/60" data-testid="cert-form-card">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Certification name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. VMware VCP-DCV 2024" data-testid="cert-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Vendor</Label>
              <Select value={form.vendor} onValueChange={v => setForm({ ...form, vendor: v })}>
                <SelectTrigger data-testid="cert-vendor"><SelectValue /></SelectTrigger>
                <SelectContent>{VENDORS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Issued on</Label>
              <Input type="date" value={form.issued_date} onChange={e => setForm({ ...form, issued_date: e.target.value })} data-testid="cert-issued" />
            </div>
            <div className="space-y-1.5">
              <Label>Expires on</Label>
              <Input type="date" value={form.expires_date} onChange={e => setForm({ ...form, expires_date: e.target.value })} data-testid="cert-expires" />
            </div>
            <div className="space-y-1.5">
              <Label>Credential ID</Label>
              <Input value={form.credential_id} onChange={e => setForm({ ...form, credential_id: e.target.value })} data-testid="cert-credential" />
            </div>
            <div className="space-y-1.5">
              <Label>Exam fee (₹, for reimbursement)</Label>
              <Input type="number" min="0" value={form.exam_fee} onChange={e => setForm({ ...form, exam_fee: e.target.value })} data-testid="cert-fee" />
            </div>
            <div className="md:col-span-3 flex justify-end">
              <Button disabled={busy} type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="cert-submit">Add</Button>
            </div>
          </form>
        </Card>
      )}

      <Tabs defaultValue={isReviewer ? "all" : "mine"}>
        <TabsList>
          {user?.employee_id && <TabsTrigger value="mine" data-testid="tab-my-certs">My certifications</TabsTrigger>}
          {isReviewer && <TabsTrigger value="all" data-testid="tab-all-certs">Team</TabsTrigger>}
        </TabsList>

        {user?.employee_id && (
          <TabsContent value="mine"><CertList items={mine} /></TabsContent>
        )}
        {isReviewer && (
          <TabsContent value="all"><CertList items={all} showName onDecide={decide} /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function topVendor(list) {
  const c = {};
  list.forEach(x => c[x.vendor] = (c[x.vendor] || 0) + 1);
  const t = Object.entries(c).sort((a, b) => b[1] - a[1])[0];
  return t ? `${t[0]} (${t[1]})` : "—";
}

function MetricCard({ icon: Icon, label, value, tone }) {
  const tones = { blue: "text-[#DC2626] bg-red-50", orange: "text-orange-600 bg-orange-50", green: "text-emerald-600 bg-emerald-50", slate: "text-slate-700 bg-slate-100" };
  return (
    <Card className="p-5 rounded-xl border-slate-200/60">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${tones[tone]}`}>
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </div>
      </div>
    </Card>
  );
}

function CertList({ items, showName, onDecide }) {
  if (!items.length) return <div className="text-sm text-slate-500 py-10 text-center border border-dashed rounded-xl">No certifications yet — add your first one.</div>;
  return (
    <div className="space-y-3">
      {items.map(c => {
        const d = daysUntil(c.expires_date);
        const expTone = d < 0 ? "text-red-600" : d <= 90 ? "text-orange-600" : "text-slate-500";
        return (
          <Card key={c.id} className="p-4 rounded-xl border-slate-200/60" data-testid={`cert-${c.id}`}>
            <div className="flex flex-wrap items-start gap-4 justify-between">
              <div className="flex gap-3 min-w-0">
                <div className="h-10 w-10 rounded-lg bg-red-50 text-[#DC2626] grid place-items-center shrink-0">
                  <Award className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  {showName && <div className="text-xs text-slate-500">{c.employee_name}</div>}
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>{c.vendor}</span>
                    {c.credential_id && <span className="font-mono">{c.credential_id}</span>}
                    <span className={`flex items-center gap-1 ${expTone}`}>
                      <Calendar className="h-3 w-3" strokeWidth={1.8} />
                      Expires {fmt(c.expires_date)} {d < 0 ? "(expired)" : d <= 90 ? `(in ${d}d)` : ""}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {c.exam_fee > 0 && (
                  <Badge className={`border-0 ${STATUS_STYLE[c.reimbursement_status]}`}>
                    ₹{c.exam_fee.toLocaleString("en-IN")} · {c.reimbursement_status}
                  </Badge>
                )}
                {onDecide && c.reimbursement_status === "Pending" && (
                  <>
                    <Button size="sm" variant="outline" className="h-8 border-red-200 text-[#DC2626]" onClick={() => onDecide(c.id, "Approved")} data-testid={`approve-cert-${c.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Approve
                    </Button>
                  </>
                )}
                {onDecide && c.reimbursement_status === "Approved" && (
                  <Button size="sm" variant="outline" className="h-8 border-emerald-200 text-emerald-700" onClick={() => onDecide(c.id, "Reimbursed")} data-testid={`reimburse-cert-${c.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Mark reimbursed
                  </Button>
                )}
                {onDecide && (c.reimbursement_status === "Pending" || c.reimbursement_status === "Approved") && (
                  <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-700" onClick={() => onDecide(c.id, "Rejected")} data-testid={`reject-cert-${c.id}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Reject
                  </Button>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
